import { useState, useEffect, useMemo, useRef } from 'react'
import { SLOT_ELIGIBILITY } from '../../lib/rosterSlots'
import { useDraftBoard, useAvailablePlayers, useMakeDraftPick, useInitDraft, useReorderDraft, useStartDraft, useStartOfflineDraft, useRealtimeDraft, useDraftQueue, useSetDraftQueue, usePauseDraft, useResumeDraft, useMakeOfflineDraftPick, useUndoDraftPick, useMyRankings, useSetAutoDraft, useCancelAutoDraft, useUpdateFantasySettings, useFantasySettings } from '../../hooks/useLeagues'
import DraftPlayerPreview from './DraftPlayerPreview'
import DraftTimezonePreview from './DraftTimezonePreview'
import { useAuth } from '../../hooks/useAuth'
import Avatar from '../ui/Avatar'
import LoadingSpinner from '../ui/LoadingSpinner'
import { toast } from '../ui/Toast'
import { initDraftAudio, playTone, buzz } from '../../lib/draftAudio'

// Settings-aware position chip list. If the commissioner configured any
// IDP slots (DL/LB/DB/S), drop DEF and add IDP filters; otherwise show DEF.
// Mirrors FantasyPlayerBrowser.buildPositionFilters and the server-side
// pool selection in fantasyService.searchAvailablePlayers.
function buildDraftPositionFilters(rosterSlots) {
  const idpCount = (rosterSlots?.dl || 0) + (rosterSlots?.lb || 0)
    + (rosterSlots?.db || 0) + (rosterSlots?.s || 0)
  const isIdp = idpCount > 0
  const base = ['All', 'QB', 'RB', 'WR', 'TE', 'K']
  // No 'S' tab — see FantasyPlayerBrowser.IDP_POSITION_FILTERS. Sleeper has
  // no live players classified S/FS/SS, so the tab was always empty; DB
  // covers corners and safeties alike.
  return isIdp ? [...base, 'DL', 'LB', 'DB'] : [...base, 'DEF']
}

const INJURY_COLORS = {
  Out: 'text-incorrect',
  IR: 'text-incorrect',
  Questionable: 'text-yellow-400',
  Probable: 'text-correct',
  'Day-To-Day': 'text-yellow-400',
}

function InjuryBadge({ status }) {
  if (!status) return null
  const label = status === 'Day-To-Day' ? 'DTD' : status === 'IR' ? 'IR' : status.charAt(0)
  return (
    <span className={`text-[12px] font-mono font-bold shrink-0 ${INJURY_COLORS[status] || 'text-text-muted'}`} title={status}>
      {label}
    </span>
  )
}

// Live countdown to a scheduled draft. Renders the absolute date in the
// user's local timezone plus a ticking d/h/m/s countdown.
function DraftCountdown({ date }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const remaining = Math.max(0, date.getTime() - now)
  const days = Math.floor(remaining / 86400000)
  const hours = Math.floor((remaining % 86400000) / 3600000)
  const mins = Math.floor((remaining % 3600000) / 60000)
  const secs = Math.floor((remaining % 60000) / 1000)
  const localDate = date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  if (remaining === 0) {
    return (
      <div className="mb-6 px-6 py-4 rounded-2xl border border-correct/40 bg-correct/10 text-correct text-base font-display text-center">
        Draft is starting any moment…
      </div>
    )
  }
  return (
    <div className="mb-6 rounded-2xl border border-accent/40 bg-accent/5 backdrop-blur-sm p-5 md:p-6">
      <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1 text-center">Draft Countdown</div>
      <div className="text-sm md:text-base text-text-primary font-semibold mb-4 text-center">{localDate}</div>
      <div className="flex items-center justify-center gap-3 md:gap-5 font-display text-text-primary">
        {days > 0 && (
          <div className="text-center">
            <div className="text-3xl md:text-5xl tabular-nums text-white">{days}</div>
            <div className="text-[10px] md:text-xs uppercase text-text-muted mt-1">days</div>
          </div>
        )}
        <div className="text-xl md:text-3xl text-text-muted/40 font-light">:</div>
        <div className="text-center">
          <div className="text-3xl md:text-5xl tabular-nums text-white">{String(hours).padStart(2, '0')}</div>
          <div className="text-[10px] md:text-xs uppercase text-text-muted mt-1">hrs</div>
        </div>
        <div className="text-xl md:text-3xl text-text-muted/40 font-light">:</div>
        <div className="text-center">
          <div className="text-3xl md:text-5xl tabular-nums text-white">{String(mins).padStart(2, '0')}</div>
          <div className="text-[10px] md:text-xs uppercase text-text-muted mt-1">min</div>
        </div>
        <div className="text-xl md:text-3xl text-text-muted/40 font-light">:</div>
        <div className="text-center">
          <div className="text-3xl md:text-5xl tabular-nums text-white">{String(secs).padStart(2, '0')}</div>
          <div className="text-[10px] md:text-xs uppercase text-text-muted mt-1">sec</div>
        </div>
      </div>
    </div>
  )
}

// Format an ISO date into the value shape <input type="datetime-local">
// expects: YYYY-MM-DDTHH:MM in the user's local zone.
function toDateTimeLocalValue(isoStr) {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// Inline draft-date editor rendered right in the pre-draft view so the
// commissioner doesn't have to detour through gear settings. Members see
// the placeholder note; commissioners get the input.
function DraftDatePrompt({ league, settings, isCommissioner, updateFantasySettings }) {
  const [localValue, setLocalValue] = useState(() => toDateTimeLocalValue(settings?.draft_date))
  if (!isCommissioner) {
    return (
      <div className="rounded-2xl border border-text-primary/15 bg-bg-primary/40 p-5 text-center">
        <div className="text-sm text-text-secondary">
          The commissioner hasn't set a draft date yet. You'll get a notification once it's scheduled.
        </div>
      </div>
    )
  }
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/5 backdrop-blur-sm p-5">
      <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-2">Schedule the Draft</div>
      <input
        type="datetime-local"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={(e) => {
          const v = e.target.value
          updateFantasySettings.mutateAsync({
            leagueId: league.id,
            draft_date: v ? new Date(v).toISOString() : null,
          }).then(() => toast('Draft time set', 'success'))
            .catch((err) => toast(err.message || 'Failed to set draft time', 'error'))
        }}
        className="w-full bg-bg-primary border border-text-primary/20 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
      />
      <DraftTimezonePreview localValue={localValue} />
      <p className="text-[11px] text-text-secondary mt-2 leading-snug">
        Shown in your local time. Leave blank to start the draft manually.
      </p>
    </div>
  )
}

export default function FantasyDraftRoom({ league }) {
  const { profile } = useAuth()
  const { data: draftData, isLoading } = useDraftBoard(league.id)
  const makePick = useMakeDraftPick()
  const makeOfflinePick = useMakeOfflineDraftPick()
  const undoPick = useUndoDraftPick()
  const [offlineMode, setOfflineMode] = useState(false)
  const initDraft = useInitDraft()
  const startDraft = useStartDraft()
  const startOffline = useStartOfflineDraft()
  const { data: queue } = useDraftQueue(league.id)
  const setQueue = useSetDraftQueue()
  const pauseDraftMut = usePauseDraft()
  const resumeDraftMut = useResumeDraft()
  const setAutoDraftMut = useSetAutoDraft()
  const cancelAutoDraftMut = useCancelAutoDraft()
  const updateFantasySettings = useUpdateFantasySettings()
  const reorderDraft = useReorderDraft()
  const [autoDraftPrompt, setAutoDraftPrompt] = useState(null)
  const presentUserIds = useRealtimeDraft(league.id, profile?.id)

  const queuedIds = useMemo(() => new Set((queue || []).map((q) => q.player_id)), [queue])

  async function toggleQueue(playerId) {
    const current = (queue || []).map((q) => q.player_id)
    const next = current.includes(playerId)
      ? current.filter((id) => id !== playerId)
      : [...current, playerId]
    try {
      await setQueue.mutateAsync({ leagueId: league.id, playerIds: next })
    } catch (err) {
      toast(err.message || 'Failed to update queue', 'error')
    }
  }

  async function moveQueue(playerId, direction) {
    const ids = (queue || []).map((q) => q.player_id)
    const idx = ids.indexOf(playerId)
    if (idx < 0) return
    const swap = direction === 'up' ? idx - 1 : idx + 1
    if (swap < 0 || swap >= ids.length) return
    ;[ids[idx], ids[swap]] = [ids[swap], ids[idx]]
    try {
      await setQueue.mutateAsync({ leagueId: league.id, playerIds: ids })
    } catch (err) {
      toast(err.message || 'Failed to reorder queue', 'error')
    }
  }

  const [posFilter, setPosFilter] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')
  const [timerSeconds, setTimerSeconds] = useState(null)
  const [activeTab, setActiveTab] = useState('Players')
  const [playerView, setPlayerView] = useState('ADP') // 'ADP' or 'My Rankings'
  const [detailPlayerId, setDetailPlayerId] = useState(null)
  const { data: myRankings } = useMyRankings(league.id)
  const pickListRef = useRef(null)

  const { data: availablePlayers } = useAvailablePlayers(
    league.id,
    // Don't search on a single character. Every keystroke is its own
    // request (each query string is a distinct React Query key), and a
    // one-letter query matches hundreds of players — the heaviest response
    // the endpoint can produce, fired first, on every search anyone types.
    searchQuery.trim().length >= 2 ? searchQuery : undefined,
    posFilter !== 'All' ? posFilter : undefined
  )

  const settings = draftData?.settings
  const picks = draftData?.picks || []
  const draftStatus = settings?.draft_status || 'pending'

  // Auto-enable offline mode when draft is in offline mode
  useEffect(() => {
    if (settings?.draft_mode === 'offline') setOfflineMode(true)
  }, [settings?.draft_mode])

  // Current pick info
  const currentPick = useMemo(() => {
    return picks.find((p) => !p.player_id) || null
  }, [picks])

  const isMyTurn = currentPick?.user_id === profile?.id
  const completedPicks = picks.filter((p) => p.player_id)
  // The queue minus anyone already drafted, computed ONCE. The tab badge and
  // the list were deriving this separately — the list filtered, the badge used
  // queue.length raw — so an emptied queue still read "Queue (2)". The
  // down-arrow bounds had the same problem, disabling on the wrong row.
  const visibleQueue = useMemo(() => {
    const drafted = new Set(completedPicks.map((p) => p.player_id))
    return (queue || []).filter((q) => !drafted.has(q.player_id))
  }, [queue, completedPicks])
  // What Undo would reverse. undoLastDraftPick targets the highest
  // pick_number, so sort rather than trusting the array's incoming order.
  const lastCompletedPick = completedPicks.length
    ? [...completedPicks].sort((a, b) => a.pick_number - b.pick_number)[completedPicks.length - 1]
    : null

  // Pick timer
  useEffect(() => {
    if (draftStatus !== 'in_progress' || !currentPick || !settings?.draft_pick_timer) {
      setTimerSeconds(null)
      return
    }

    // Mirror the SERVER's deadline baseline exactly. processDraftAutopicks
    // uses max(last pick, draft_started_at, draft_resumed_at, draft_date);
    // this used only the last pick, so the two disagreed whenever anything
    // other than a pick restarted the clock:
    //
    //   After a pause, the entire pause counted as elapsed time. Pause five
    //   minutes on a 60s timer and the next player's clock read 0:00 while
    //   the server was actually giving them a full 60s from draft_resumed_at
    //   -- reported live on 2026-09-04 as "the next guy only had 10 seconds".
    //
    //   Opening the room before the scheduled time burned pick 1's clock
    //   immediately, then sat at 0:00, and never recovered: draft_date wasn't
    //   in the dependency list, so nothing recomputed at the start time.
    const ts = (v) => (v ? new Date(v).getTime() : 0)
    const lastPick = completedPicks[completedPicks.length - 1]
    const baseline = Math.max(
      ts(lastPick?.picked_at),
      ts(settings.draft_started_at),
      ts(settings.draft_resumed_at),
      ts(settings.draft_date),
    ) || Date.now()

    // Recompute from the baseline each tick rather than decrementing, so the
    // clock self-corrects after a backgrounded tab or a sleeping phone
    // instead of drifting. Clamped to the timer so a not-yet-reached
    // draft_date shows a full clock rather than more than one.
    const compute = () => {
      const elapsed = (Date.now() - baseline) / 1000
      return Math.ceil(Math.min(settings.draft_pick_timer, Math.max(0, settings.draft_pick_timer - elapsed)))
    }

    setTimerSeconds(compute())
    const interval = setInterval(() => setTimerSeconds(compute()), 1000)

    return () => clearInterval(interval)
  }, [
    draftStatus,
    currentPick?.pick_number,
    settings?.draft_pick_timer,
    settings?.draft_started_at,
    settings?.draft_resumed_at,
    settings?.draft_date,
    completedPicks.length,
  ])

  // Tick beeps during the final countdown of YOUR pick. Fires once at
  // 10s remaining (gentle warning), then short blips at 5/4/3/2/1 with
  // rising pitch so the urgency escalates audibly.
  const lastBeepedSecondRef = useRef(null)
  useEffect(() => {
    if (!isMyTurn || draftStatus !== 'in_progress' || timerSeconds == null) {
      lastBeepedSecondRef.current = null
      return
    }
    const triggers = { 10: { freq: 660, dur: 0.25, gain: 0.1 },
                       5:  { freq: 880, dur: 0.18, gain: 0.13 },
                       4:  { freq: 880, dur: 0.18, gain: 0.13 },
                       3:  { freq: 1100, dur: 0.18, gain: 0.15 },
                       2:  { freq: 1100, dur: 0.18, gain: 0.15 },
                       1:  { freq: 1320, dur: 0.25, gain: 0.18 } }
    const trig = triggers[timerSeconds]
    if (!trig) return
    if (lastBeepedSecondRef.current === timerSeconds) return
    lastBeepedSecondRef.current = timerSeconds
    playTone(trig)
    // Final three seconds also buzz, for a phone on silent.
    if (timerSeconds <= 3) buzz([90])
  }, [isMyTurn, timerSeconds, draftStatus])

  // "You're on the clock" in-room alert — fires only when isMyTurn flips
  // false → true. Plays a beep, toasts, and flashes the document title so
  // users with the tab in the background notice.
  // "The draft is live" alert. Fires on any transition INTO in_progress, so
  // it covers both the scheduled start and a resume after a pause — in both
  // cases people have been staring at a static screen and need to know the
  // clock is running again.
  //
  // Initialised from the CURRENT status so opening a room mid-draft doesn't
  // announce a start that already happened.
  const wasLiveRef = useRef(draftStatus === 'in_progress')
  useEffect(() => {
    const isLive = draftStatus === 'in_progress'
    if (isLive && !wasLiveRef.current) {
      // Three ascending notes — longer and more announcement-like than the
      // two-note pick confirmation, and unlike the countdown ticks, which
      // mean the opposite thing.
      playTone({ freq: 523, dur: 0.14, gain: 0.12 })
      setTimeout(() => playTone({ freq: 659, dur: 0.14, gain: 0.12 }), 150)
      setTimeout(() => playTone({ freq: 880, dur: 0.3, gain: 0.14 }), 300)
      buzz([200, 90, 200])
      toast('The draft is live!', 'success')
    }
    wasLiveRef.current = isLive
  }, [draftStatus])

  const wasMyTurnRef = useRef(false)
  // The tab title before any flashing. Captured once at mount: the old code
  // read document.title at flash time, so a flash starting while a previous
  // one was mid-cycle captured "⏰ YOU'RE UP" as the thing to restore, and
  // every later restore put the alarm clock back permanently — including
  // after the draft ended.
  const baseTitleRef = useRef(typeof document !== 'undefined' ? document.title : '')
  useEffect(() => {
    const fire = isMyTurn && !wasMyTurnRef.current && draftStatus === 'in_progress'
    wasMyTurnRef.current = isMyTurn
    if (!fire) return undefined
    toast("You're on the clock!", 'success')
    playTone({ freq: 880, dur: 0.4, gain: 0.15 })
    // Buzz too: iOS Web Audio obeys the physical silent switch inside a
    // Capacitor WebView, so a muted phone hears nothing. Vibration is the
    // only alert that reaches someone with their ringer off — which, at a
    // draft, is most people.
    buzz([160, 80, 160])
    const original = baseTitleRef.current
    let flashCount = 0
    const flashTimer = setInterval(() => {
      document.title = flashCount % 2 === 0 ? "⏰ YOU'RE UP" : original
      flashCount++
      if (flashCount > 10) {
        clearInterval(flashTimer)
        document.title = original
      }
    }, 800)
    // Cleanup runs when the turn ends, the draft status changes, or the room
    // unmounts — so the alarm stops as soon as the pick is in, instead of
    // flashing out its full eight seconds at someone who has already picked.
    return () => {
      clearInterval(flashTimer)
      document.title = original
    }
  }, [isMyTurn, draftStatus])

  // Auto-scroll to latest pick
  useEffect(() => {
    if (pickListRef.current) {
      pickListRef.current.scrollTop = pickListRef.current.scrollHeight
    }
  }, [completedPicks.length])

  const isCommissioner = league.commissioner_id === profile?.id

  // Commissioner: detect autopicks and prompt to flag user as autodrafting.
  //
  // This used to look at ONLY the most recent pick. Picks arrive in batches
  // — the board polls every 5s and an autopick sweep can land two in that
  // window — so when several autopicks came in together, every one but the
  // last was silently dropped and that manager was never flagged. Seen live
  // 2026-09-05: BallKnower was autopicked from their queue and no prompt
  // ever appeared, because the next autopick overwrote it.
  //
  // Now every new pick since the last check is scanned, and each manager is
  // asked about at most once per draft — so dismissing a prompt with "No"
  // doesn't mean being re-asked on their next miss.
  const prevPickCountRef = useRef(completedPicks.length)
  const promptedUsersRef = useRef(new Set())
  useEffect(() => {
    if (!isCommissioner || draftStatus !== 'in_progress') return
    const seen = prevPickCountRef.current
    if (completedPicks.length <= seen) {
      prevPickCountRef.current = completedPicks.length
      return
    }
    const fresh = completedPicks.slice(seen)
    prevPickCountRef.current = completedPicks.length

    const flagged = settings?.auto_drafting_users || []
    const candidate = fresh.find((p) => (
      p?.is_auto_pick
      && !flagged.includes(p.user_id)
      && !promptedUsersRef.current.has(p.user_id)
    ))
    if (!candidate) return

    promptedUsersRef.current.add(candidate.user_id)
    setAutoDraftPrompt({
      userId: candidate.user_id,
      displayName: candidate.users?.display_name || candidate.users?.username || 'Unknown',
      pickNumber: candidate.pick_number,
    })
  }, [completedPicks.length])

  const amAutoDrafting = (settings?.auto_drafting_users || []).includes(profile?.id)
  const onClockIsAutoDrafting = currentPick && (settings?.auto_drafting_users || []).includes(currentPick.user_id)

  async function handlePick(playerId) {
    if (offlineMode) {
      if (makeOfflinePick.isPending) return
      try {
        await makeOfflinePick.mutateAsync({ leagueId: league.id, playerId })
        setSearchQuery('')
      } catch (err) {
        toast(err.message || 'Failed to record offline pick', 'error')
      }
      return
    }
    if (!isMyTurn || makePick.isPending) return
    try {
      await makePick.mutateAsync({ leagueId: league.id, playerId })
      setSearchQuery('')
      // Confirmation for your own pick. Deliberately unlike the countdown
      // ticks — those escalate in pitch to signal pressure, so reusing that
      // sound to say "done" would read as more urgency at the moment the
      // pressure ends. This is a short rising two-note figure instead:
      // clearly a completion, not a warning.
      playTone({ freq: 660, dur: 0.09, gain: 0.11 })
      setTimeout(() => playTone({ freq: 990, dur: 0.16, gain: 0.11 }), 95)
      buzz([40])
    } catch (err) {
      toast(err.message || 'Failed to make pick', 'error')
    }
  }

  async function handleInitDraft() {
    try {
      await initDraft.mutateAsync(league.id)
      toast('Draft order randomized!', 'success')
    } catch (err) {
      toast(err.message || 'Failed to initialize draft', 'error')
    }
  }

  async function handleStartDraft() {
    try {
      await startDraft.mutateAsync(league.id)
      toast('Draft started!', 'success')
    } catch (err) {
      toast(err.message || 'Failed to start draft', 'error')
    }
  }

  if (isLoading) return <LoadingSpinner />

  // Pre-draft state
  if (draftStatus === 'pending') {
    const hasPickSlots = picks.length > 0
    const draftDate = settings?.draft_date ? new Date(settings.draft_date) : null
    const draftDateValid = draftDate && !isNaN(draftDate.getTime())
    const msUntilDraft = draftDateValid ? draftDate.getTime() - Date.now() : Infinity
    const withinOneHour = hasPickSlots && draftDateValid && msUntilDraft <= 60 * 60 * 1000 && msUntilDraft > 0
    // Scheduled time has passed but the server hasn't flipped us to
    // in_progress yet — the auto-start tick is up to 10s behind.
    const draftTimeReached = draftDateValid && msUntilDraft <= 0

    // ── Board preview with countdown (T-60min) ──────────────────────
    if (withinOneHour) {
      return <DraftBoardPreview settings={settings} picks={picks} draftDate={draftDate} profileId={profile?.id} league={league} isCommissioner={isCommissioner} onStartDraft={handleStartDraft} startDraftPending={startDraft.isPending} presentUserIds={presentUserIds} />
    }

    const numTeams = settings?.num_teams || 10
    const memberCount = league.members?.length || 0
    const rounds = settings?.roster_slots ? Object.entries(settings.roster_slots).reduce((sum, [k, v]) => sum + (k === 'ir' ? 0 : v), 0) : (picks.length / numTeams || 15)
    const scoringLabel = settings?.scoring_format === 'ppr' ? 'PPR' : settings?.scoring_format === 'half_ppr' ? 'Half-PPR' : 'Standard'
    const isSnake = true // default draft type

    return (
      <div className="max-w-xl mx-auto py-6 md:py-8 space-y-6">
        <div className="text-center">
          <h3 className="font-display text-2xl md:text-3xl text-text-primary mb-2">
            {settings?.draft_mode === 'offline' ? 'Offline Draft' : 'Draft Room'}
          </h3>
          <p className="text-sm text-text-primary">
            {hasPickSlots
              ? `Draft order is set. ${memberCount} teams, ${rounds} rounds, ${picks.length} total picks.`
              : `${memberCount} teams joined. The commissioner needs to set the draft order.`}
          </p>
        </div>

        {settings?.draft_location && (
          <div className="text-center text-sm text-text-secondary">
            <span className="text-text-muted">Location:</span> <span className="font-semibold text-text-primary">{settings.draft_location}</span>
          </div>
        )}

        {draftDateValid && settings?.draft_mode === 'offline' ? (
          <div className="rounded-2xl border border-accent/40 bg-accent/5 backdrop-blur-sm p-5 text-center">
            <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1">Draft Date</div>
            <div className="text-base text-text-primary font-semibold">
              {draftDate.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
            </div>
          </div>
        ) : draftDateValid ? (
          <DraftCountdown date={draftDate} />
        ) : (
          <DraftDatePrompt league={league} settings={settings} isCommissioner={isCommissioner} updateFantasySettings={updateFantasySettings} />
        )}

        {/* Commissioner actions */}
        <div className="text-center space-y-3">
          {isCommissioner && !hasPickSlots && (() => {
            const isFull = memberCount >= numTeams
            return (
              <>
                <button
                  onClick={handleInitDraft}
                  disabled={initDraft.isPending || !isFull}
                  className="px-6 py-3 rounded-xl text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
                >
                  {initDraft.isPending ? 'Randomizing...' : 'Randomize Draft Order'}
                </button>
                {!isFull && (
                  <p className="text-xs text-text-muted">
                    {memberCount}/{numTeams} members joined — league must be full before setting draft order.
                  </p>
                )}
              </>
            )
          })()}
          {isCommissioner && hasPickSlots && settings?.draft_mode === 'offline' && (
            <button
              onClick={async () => {
                try {
                  await startOffline.mutateAsync(league.id)
                  toast('Offline draft started — enter your results', 'success')
                } catch (err) {
                  toast(err.message || 'Failed to start offline draft', 'error')
                }
              }}
              disabled={startOffline.isPending}
              className="px-6 py-3 rounded-xl text-sm font-semibold bg-correct text-white hover:bg-correct/80 transition-colors disabled:opacity-50"
            >
              {startOffline.isPending ? 'Starting...' : 'Enter Draft Results'}
            </button>
          )}
          {isCommissioner && hasPickSlots && settings?.draft_mode !== 'offline' && (
            // Once the scheduled time passes, the server starts this draft on
            // its own — but the autopick loop only ticks every 10s, and the
            // countdown view disappears the instant the clock hits zero. That
            // left the commissioner staring at a bare "Start Draft" button
            // during the gap, reading it as "this needs me". On 2026-09-04 the
            // commissioner beat the server to it by 298ms and assumed
            // auto-start was broken. The button stays (it's the escape hatch
            // if the loop is wedged) but no longer implies action is required.
            <div className="flex flex-col items-center gap-1.5">
              <button
                onClick={handleStartDraft}
                disabled={startDraft.isPending}
                className="px-6 py-3 rounded-xl text-sm font-semibold bg-correct text-white hover:bg-correct/80 transition-colors disabled:opacity-50"
              >
                {startDraft.isPending ? 'Starting...' : draftTimeReached ? 'Start Now' : 'Start Draft'}
              </button>
              {draftTimeReached && (
                <span className="text-[11px] text-text-muted">Starting automatically — no action needed</span>
              )}
            </div>
          )}
        </div>

        {/* Draft order */}
        {hasPickSlots && (() => {
          const currentOrder = settings?.draft_order || []
          const canReorder = isCommissioner && draftStatus === 'pending' && settings?.draft_mode === 'offline'
          const moveSlot = async (fromIdx, dir) => {
            const toIdx = fromIdx + dir
            if (toIdx < 0 || toIdx >= currentOrder.length) return
            const next = [...currentOrder]
            ;[next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]]
            try {
              await reorderDraft.mutateAsync({ leagueId: league.id, order: next })
            } catch (err) {
              toast(err.message || 'Failed to reorder', 'error')
            }
          }
          return (
            <div className="rounded-xl border border-text-primary/20 bg-bg-primary/60 backdrop-blur-sm p-4">
              <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Draft Order</div>
              <div className="space-y-1.5">
                {currentOrder.map((userId, i) => {
                  const member = picks.find((p) => p.user_id === userId)?.users
                  const isYou = userId === profile?.id
                  return (
                    <div key={userId} className={`flex items-center gap-2.5 py-1.5 px-2 rounded-lg ${isYou ? 'bg-accent/10' : ''}`}>
                      <span className={`text-sm font-display w-6 ${isYou ? 'text-accent' : 'text-text-muted'}`}>{i + 1}</span>
                      {member && <Avatar user={member} size="xs" />}
                      <span className={`flex-1 text-sm truncate ${isYou ? 'text-accent font-semibold' : 'text-text-primary'}`}>{member?.display_name || member?.username || 'Unknown'}{isYou ? ' (you)' : ''}</span>
                      {canReorder && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => moveSlot(i, -1)}
                            disabled={i === 0 || reorderDraft.isPending}
                            className="w-7 h-7 rounded-md text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            aria-label="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSlot(i, 1)}
                            disabled={i === currentOrder.length - 1 || reorderDraft.isPending}
                            className="w-7 h-7 rounded-md text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* Pre-draft player browser — visible once draft order is set */}
        {hasPickSlots && <PreDraftBrowser leagueId={league.id} />}

        {/* How it works */}
        <div className="rounded-xl border border-text-primary/20 bg-bg-primary/60 backdrop-blur-sm p-4">
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">How the Draft Works</div>
          <ul className="space-y-2.5 text-sm text-text-primary">
            <li className="flex gap-3">
              <span className="text-accent font-bold shrink-0">→</span>
              <span>This is a <span className="font-semibold">snake draft</span> — the order reverses each round so the last pick in round 1 gets the first pick in round 2.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold shrink-0">→</span>
              <span>Each pick is on a <span className="font-semibold">{settings?.draft_pick_timer || 90}-second timer</span>. If time runs out, the autodraft function defers to the best available player from your queue. If no queue is set, autodraft looks to your custom rankings. If none are set, then the next best player based on ADP is drafted. If you know you can't attend the draft live, then be sure to spend time customizing your rankings so that the autodraft will go off of that.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold shrink-0">→</span>
              <span>Scoring format: <span className="font-semibold">{scoringLabel}</span>. {numTeams} teams, {rounds} rounds.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold shrink-0">→</span>
              <span>Use the <span className="font-semibold">Mock Draft</span> tab to practice before the real thing.</span>
            </li>
          </ul>
        </div>
      </div>
    )
  }

  // Paused state
  if (draftStatus === 'paused') {
    return (
      <div className="text-center py-8 space-y-4">
        <div className="text-4xl">⏸</div>
        <h3 className="font-display text-lg text-text-primary">Draft Paused</h3>
        <p className="text-sm text-text-secondary">The commissioner has paused this draft. The pick clock is frozen.</p>
        {isCommissioner && (
          <button
            onClick={async () => {
              try { await resumeDraftMut.mutateAsync(league.id); toast('Draft resumed', 'success') }
              catch (err) { toast(err.message || 'Failed to resume', 'error') }
            }}
            disabled={resumeDraftMut.isPending}
            className="px-6 py-2 rounded-xl text-sm font-semibold bg-correct text-white hover:bg-correct/80 transition-colors disabled:opacity-50"
          >
            {resumeDraftMut.isPending ? 'Resuming...' : 'Resume Draft'}
          </button>
        )}
      </div>
    )
  }

  // Draft completed state — review screen with team accordion + board
  if (draftStatus === 'completed') {
    return (
      <CompletedDraftReview
        league={league}
        picks={picks}
        settings={settings}
        profileId={profile?.id}
      />
    )
  }

  // Offline draft entry — clean commissioner-only UI
  if (settings?.draft_mode === 'offline') {
    const totalPicks = picks.length
    const filledPicks = completedPicks.length
    const progressPct = totalPicks > 0 ? Math.round((filledPicks / totalPicks) * 100) : 0
    const numTeams = settings?.num_teams || 10

    return (
      <div className="space-y-4">
        {/* Progress header */}
        <div className="rounded-xl border border-text-primary/20 bg-bg-primary p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-text-muted">Entering Draft Results</div>
            <div className="text-xs text-text-muted">{filledPicks} / {totalPicks} picks</div>
          </div>
          <div className="w-full h-2 bg-bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Current pick info */}
        {currentPick && (
          <div className="rounded-xl border border-accent/40 bg-accent/5 p-4 text-center">
            <div className="font-display text-lg text-text-primary">
              Round {currentPick.round} · Pick {((currentPick.pick_number - 1) % numTeams) + 1}
            </div>
            <div className="flex items-center justify-center gap-2 mt-2">
              {currentPick.users && <Avatar user={currentPick.users} size="sm" />}
              <span className="text-sm font-semibold text-accent">
                {currentPick.users?.display_name || currentPick.users?.username || 'Unknown'}
              </span>
            </div>
          </div>
        )}

        {/* Undo button. Commissioner-gated in the UI to match the server,
            which 403s everyone else — without this a regular member saw an
            Undo button that only ever produced an error toast. */}
        {isCommissioner && lastCompletedPick && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-text-muted">
              Last pick: <span className="text-text-primary font-semibold">{lastCompletedPick.nfl_players?.full_name}</span>
              {' '}to {lastCompletedPick.users?.display_name || lastCompletedPick.users?.username}
            </div>
            <button
              onClick={async () => {
                try {
                  await undoPick.mutateAsync(league.id)
                  toast('Pick undone', 'success')
                } catch (err) {
                  toast(err.message || 'Failed to undo', 'error')
                }
              }}
              disabled={undoPick.isPending}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-incorrect border border-incorrect/30 hover:bg-incorrect/10 transition-colors disabled:opacity-50"
            >
              {undoPick.isPending ? 'Undoing...' : 'Undo'}
            </button>
          </div>
        )}

        {/* Player search + filter */}
        {currentPick && (
          <>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Search players..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg bg-bg-primary border border-text-primary/20 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {buildDraftPositionFilters(settings?.roster_slots).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setPosFilter(pos)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    posFilter === pos ? 'bg-accent text-white' : 'bg-bg-card text-text-muted border border-text-primary/10 hover:bg-bg-secondary'
                  }`}
                >
                  {pos}
                </button>
              ))}
            </div>

            {/* Player list */}
            <div className="rounded-xl border border-text-primary/20 overflow-hidden max-h-[50vh] overflow-y-auto">
              {(availablePlayers || []).map((player) => (
                <div
                  key={player.id}
                  className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 hover:bg-accent/10 transition-colors"
                >
                  {player.headshot_url ? (
                    <img src={player.headshot_url} alt="" className="w-8 h-8 rounded-full object-cover bg-bg-secondary shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-bg-secondary shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-text-primary truncate">{player.full_name}</div>
                    <div className="text-[10px] text-text-muted">{player.position} · {player.team || 'FA'}</div>
                  </div>
                  <InjuryBadge status={player.injury_status} />
                  <button
                    onClick={() => handlePick(player.id)}
                    disabled={makeOfflinePick.isPending}
                    className="shrink-0 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-accent text-white hover:bg-accent-hover active:scale-95 transition disabled:opacity-50"
                  >
                    Draft
                  </button>
                </div>
              ))}
              {(!availablePlayers || availablePlayers.length === 0) && (
                <div className="text-center text-sm text-text-muted py-8">No players found</div>
              )}
            </div>
          </>
        )}

        {/* Recent picks log (collapsed) */}
        {filledPicks > 0 && (
          <div className="rounded-xl border border-text-primary/20 overflow-hidden">
            <div className="p-3 border-b border-border">
              <h3 className="text-sm font-semibold text-text-primary">Draft Log ({filledPicks} picks)</h3>
            </div>
            <DraftLogList completedPicks={completedPicks} numTeams={numTeams} profileId={profile?.id} listRef={pickListRef} />
          </div>
        )}
      </div>
    )
  }

  // Build my roster slot map from commissioner's roster_slots config
  const myPicks = completedPicks.filter((p) => p.user_id === profile?.id)
  const rosterSlots = settings?.roster_slots || { qb: 1, rb: 2, wr: 2, te: 1, flex: 1, k: 1, def: 1, bench: 6 }
  const slotPlan = buildSlotPlan(rosterSlots, myPicks)

  // Live draft
  return (
    <div className="space-y-3 md:space-y-4">
      {/* "I'm here" banner for autodrafting users */}
      {amAutoDrafting && (
        <div className="rounded-xl border border-yellow-500/40 bg-yellow-500/10 px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-yellow-400">You are set to autodraft. Your picks are being made automatically.</span>
          <button
            onClick={async () => {
              try {
                await cancelAutoDraftMut.mutateAsync(league.id)
                toast("Welcome back! You're drafting manually now.", 'success')
              } catch (err) { toast(err.message || 'Failed', 'error') }
            }}
            disabled={cancelAutoDraftMut.isPending}
            className="shrink-0 px-4 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500 text-black hover:bg-yellow-400 transition-colors disabled:opacity-50"
          >
            I'm here
          </button>
        </div>
      )}

      {/* Sticky pick banner — glass edge */}
      <div className="sticky top-0 z-20 -mx-2 px-2 pt-1 md:static md:mx-0 md:px-0 md:pt-0">
      <div className={`rounded-xl px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap bg-bg-primary border transition-all ${
        isMyTurn
          ? 'border-2 border-accent bg-accent/5 shadow-[0_0_24px_rgba(251,146,60,0.5)]'
          : 'border-text-primary/20'
      }`}>
        <div className="font-display text-base md:text-lg text-white">
          R{currentPick?.round} · PICK {currentPick?.pick_number}
        </div>
        <div className={`font-display text-sm md:text-base ${isMyTurn ? 'text-accent font-bold uppercase tracking-wider' : 'text-text-secondary'}`}>
          {isMyTurn ? "🔔 You're on the clock!" : `${currentPick?.users?.display_name || 'Someone'} is picking...`}
        </div>
        {onClockIsAutoDrafting ? (
          <div className="font-display text-base md:text-lg text-yellow-400">AUTO</div>
        ) : timerSeconds != null ? (
          <div className={`font-display ${
            timerSeconds <= 15
              ? 'text-2xl md:text-3xl text-incorrect animate-pulse'
              : 'text-base md:text-lg text-text-primary'
          }`}>
            {Math.floor(timerSeconds / 60)}:{String(timerSeconds % 60).padStart(2, '0')}
          </div>
        ) : null}
        {isCommissioner && (
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={async () => {
                try { await pauseDraftMut.mutateAsync(league.id); toast('Draft paused', 'success') }
                catch (err) { toast(err.message || 'Failed to pause', 'error') }
              }}
              disabled={pauseDraftMut.isPending}
              className="px-3 py-1 rounded-lg text-xs font-semibold bg-bg-card hover:bg-bg-secondary text-text-secondary border border-text-primary/20 transition-colors disabled:opacity-50"
            >
              {pauseDraftMut.isPending ? 'Pausing...' : 'Pause draft'}
            </button>
            <button
              onClick={() => setOfflineMode((v) => !v)}
              className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                offlineMode
                  ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                  : 'bg-bg-card hover:bg-bg-secondary border-text-primary/20 text-text-secondary'
              }`}
              title="Record picks for whoever's on the clock — for in-person drafts"
            >
              {offlineMode ? '● Offline mode ON' : 'Offline mode'}
            </button>
            {/* Undo the last pick mid-draft. Previously this only existed in
                the offline results-entry flow, so a commissioner in a LIVE
                draft could freeze the clock but had no way to fix a bad pick.
                Confirm names the player and manager so it can't be misfired
                on a fast-moving board. */}
            {lastCompletedPick && (
              <button
                onClick={async () => {
                  const who = lastCompletedPick.users?.display_name || lastCompletedPick.users?.username || 'that manager'
                  const what = lastCompletedPick.nfl_players?.full_name || 'the last pick'
                  if (!confirm(`Undo ${what} to ${who}? The pick goes back on the clock and the player returns to the pool.`)) return
                  try {
                    await undoPick.mutateAsync(league.id)
                    toast('Pick undone', 'success')
                  } catch (err) {
                    toast(err.message || 'Failed to undo', 'error')
                  }
                }}
                disabled={undoPick.isPending}
                className="px-3 py-1 rounded-lg text-xs font-semibold text-incorrect border border-incorrect/30 hover:bg-incorrect/10 transition-colors disabled:opacity-50"
                title={`Undo ${lastCompletedPick.nfl_players?.full_name || 'the last pick'}`}
              >
                {undoPick.isPending ? 'Undoing...' : 'Undo last pick'}
              </button>
            )}
          </div>
        )}
        {/* Everyone sees the last pick, not just the commissioner.
            Reported after the 2026-09-07 JMI draft as "can't see it on the
            phone" — but it was never a screen-size gate. This was
            isCommissioner-only, so the only people who ever saw it were
            commissioners, who happened to be on a laptop. On a phone the Log
            tab is a deliberate navigation away from the board, which is the
            last thing you want while your own clock is running.
            For a commissioner it doubles as "here is what Undo reverses". */}
        {lastCompletedPick && (
          <div className="mt-1.5 text-[11px] text-text-muted w-full text-center">
            Last pick: <span className="text-text-primary font-semibold">{lastCompletedPick.nfl_players?.full_name}</span>
            {' '}to {lastCompletedPick.users?.display_name || lastCompletedPick.users?.username}
          </div>
        )}
        {offlineMode && (
          <div className="mt-2 text-[11px] text-yellow-400">
            Recording for {currentPick?.users?.display_name || 'on-the-clock user'} · turn check disabled
          </div>
        )}
      </div>
      </div>

      {/* Embedded player preview (replaces the old modal) */}
      {detailPlayerId && (
        <DraftPlayerPreview
          leagueId={league.id}
          playerId={detailPlayerId}
          onClose={() => setDetailPlayerId(null)}
          onDraft={(isMyTurn || offlineMode)
            ? async () => { await handlePick(detailPlayerId); setDetailPlayerId(null) }
            : null}
          onQueue={() => toggleQueue(detailPlayerId)}
          isQueued={queuedIds.has(detailPlayerId)}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-text-primary/10">
        {['Players', 'My Roster', 'Board', 'Queue', 'Log'].map((t) => {
          const isActive = activeTab === t
          const filledCount = slotPlan.filled
          const totalCount = slotPlan.totalRoster
          const badge = t === 'My Roster' ? `${filledCount}/${totalCount}` : t === 'Queue' ? visibleQueue.length : null
          return (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                isActive ? 'border-accent text-accent' : 'border-transparent text-text-muted hover:text-text-primary'
              }`}
            >
              {t}{badge != null ? ` (${badge})` : ''}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'Players' && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Player search + list */}
        <div className="rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="p-3 border-b border-border">
            <div className="flex gap-1 mb-2">
              {['ADP', 'My Rankings'].map((v) => (
                <button
                  key={v}
                  onClick={() => setPlayerView(v)}
                  className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-colors ${
                    playerView === v ? 'bg-accent text-white' : 'bg-bg-secondary text-text-secondary'
                  }`}
                >{v}</button>
              ))}
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search players..."
              className="w-full bg-bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <div className="flex gap-1 mt-2 overflow-x-auto">
              {buildDraftPositionFilters(settings?.roster_slots).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setPosFilter(pos)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                    posFilter === pos ? 'bg-accent text-white' : 'bg-bg-secondary text-text-secondary hover:bg-border'
                  }`}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[55vh] md:max-h-96 overflow-y-auto">
            {(() => {
              // Drafted players are excluded server-side, but only as of the
              // last fetch of this list — and useAvailablePlayers refetches
              // on a 120s interval, relying on the realtime invalidation in
              // between. When that event is slow or the socket has
              // reconnected (routine on a phone), the ADP tab kept showing
              // players who were already gone: observed 15-20s on 2026-09-05,
              // and one who never cleared at all.
              //
              // `picks` comes from useDraftBoard, which polls every 5s, so
              // filtering against it here bounds staleness at 5 seconds no
              // matter what realtime does. My Rankings already did this,
              // which is exactly why that tab looked correct and this one
              // didn't. Trust the data you're already polling, not an event
              // that may not arrive.
              const draftedSet = new Set(picks.filter((p) => p.player_id).map((p) => p.player_id))

              // Default = ADP view: use the overall_rank from the API.
              let displayList = (availablePlayers || [])
                .filter((p) => !draftedSet.has(p.id))
                .map((p) => ({ ...p, _displayRank: p.overall_rank || null }))

              if (playerView === 'My Rankings' && myRankings?.length) {
                // Build user-rank lookup (filtering out drafted entries first)
                const userRankIdx = {} // player_id → 1-based rank
                let i = 0
                for (const r of myRankings) {
                  if (draftedSet.has(r.player_id)) continue
                  i++
                  userRankIdx[r.player_id] = i
                }
                const inLeague = new Set(displayList.map((p) => p.id))
                const ranked = []
                const seenIds = new Set()
                // Walk myRankings in order, picking out the ones currently available
                for (const r of myRankings) {
                  if (draftedSet.has(r.player_id)) continue
                  if (!inLeague.has(r.player_id)) continue
                  const p = displayList.find((x) => x.id === r.player_id)
                  if (p) {
                    ranked.push({ ...p, _displayRank: userRankIdx[r.player_id] })
                    seenIds.add(p.id)
                  }
                }
                const tail = displayList
                  .filter((p) => !seenIds.has(p.id))
                  .map((p) => ({ ...p, _displayRank: null }))
                displayList = [...ranked, ...tail]
              }
              return displayList
            })().map((player) => {
              const isQueued = queuedIds.has(player.id)
              return (
                <div
                  key={player.id}
                  className={`w-full flex items-center gap-3 px-3 py-3 md:py-2 border-b border-border last:border-0 transition-colors ${
                    (isMyTurn || offlineMode) ? 'hover:bg-accent/10' : 'opacity-60'
                  }`}
                >
                  <span className="w-8 text-center text-xs font-bold text-text-muted shrink-0">
                    {player._displayRank || '—'}
                  </span>
                  <button
                    onClick={() => toggleQueue(player.id)}
                    className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-lg active:bg-bg-secondary transition-colors ${isQueued ? 'text-yellow-400' : 'text-text-muted hover:text-yellow-400'}`}
                    title={isQueued ? 'Remove from queue' : 'Add to queue'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill={isQueued ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDetailPlayerId(player.id)}
                    className="flex-1 flex items-center gap-3 text-left cursor-pointer"
                  >
                    <img
                      src={player.headshot_url}
                      alt={player.full_name}
                      width="40"
                      height="40"
                      loading="lazy"
                      decoding="async"
                      className="w-10 h-10 rounded-full object-cover bg-bg-secondary shrink-0"
                      onError={(e) => { e.target.style.visibility = 'hidden' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-text-primary truncate">{player.full_name}</span>
                        <InjuryBadge status={player.injury_status} />
                      </div>
                      <div className="text-xs text-text-muted flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${posColor(player.position) || 'text-text-muted'}`}>
                          {/* Position only. The appended pos_rank was a rank
                              among players still AVAILABLE, so it renumbered
                              after every pick — the same player was RB2, then
                              RB1, then RB2 again as others came off the board.
                              A badge that changes meaning mid-draft is worse
                              than no number at all. */}
                          {player.position}
                        </span>
                        <span>{player.team || 'FA'}</span>
                        {player.bye_week && <span className="text-text-muted">· Bye {player.bye_week}</span>}
                      </div>
                    </div>
                    {player.projected_pts_half_ppr != null && Number(player.projected_pts_half_ppr) > 0 && (
                      <div className="text-right shrink-0">
                        <div className="text-sm font-display text-accent">{Number(player.projected_pts_half_ppr).toFixed(1)}</div>
                        <div className="text-[10px] text-text-muted">proj</div>
                      </div>
                    )}
                  </button>
                  {(isMyTurn || offlineMode) && (
                    <button
                      onClick={() => handlePick(player.id)}
                      disabled={makePick.isPending || makeOfflinePick.isPending}
                      className="shrink-0 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-accent text-white hover:bg-accent-hover active:scale-95 transition disabled:opacity-50"
                    >
                      Draft
                    </button>
                  )}
                </div>
              )
            })}
            {availablePlayers?.length === 0 && (
              <div className="text-center text-sm text-text-muted py-8">No players found</div>
            )}
          </div>
        </div>

        {/* Desktop-only side panel: log */}
        <div className="hidden md:block rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-semibold text-text-primary">Draft Log</h3>
          </div>
          <DraftLogList completedPicks={completedPicks} numTeams={settings?.num_teams || 10} profileId={profile?.id} listRef={pickListRef} />
        </div>
      </div>
      )}

      {activeTab === 'My Roster' && (
        <RosterNeedsView slotPlan={slotPlan} />
      )}

      {activeTab === 'Board' && (
        <div
          className="rounded-xl border border-text-primary/20 p-2 overflow-hidden md:relative md:left-1/2 md:-translate-x-1/2 md:w-[95vw] md:max-w-[1600px]"
        >
          <DraftBoard picks={picks} settings={settings} profileId={profile?.id} presentUserIds={presentUserIds} />
        </div>
      )}

      {activeTab === 'Queue' && (() => {
        // visibleQueue is computed once at component scope so the tab badge,
        // this list and the reorder bounds cannot drift apart again.
        return (
        <div className="rounded-xl border border-text-primary/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">My Queue</h3>
            <span className="text-[10px] text-text-muted italic">Auto-picks from here when your clock runs out</span>
          </div>
          {visibleQueue.length === 0 ? (
            <p className="text-xs text-text-muted">Star players in the Players tab to queue them up.</p>
          ) : (
            <div className="space-y-1">
              {visibleQueue.map((q, i) => (
                <div key={q.player_id} className="flex items-center gap-2 bg-bg-card rounded-lg px-2 py-1.5">
                  <span className="text-xs text-text-muted w-5 text-center shrink-0">{i + 1}</span>
                  {q.nfl_players?.headshot_url && (
                    <img src={q.nfl_players.headshot_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-text-primary truncate">{q.nfl_players?.full_name}</div>
                    <div className="text-[10px] text-text-muted">{q.nfl_players?.position} · {q.nfl_players?.team || 'FA'}</div>
                  </div>
                  {/* On the clock, the queue's job is to be drafted FROM —
                      people went to it expecting to pick there and found only
                      reorder arrows. Draft replaces those arrows rather than
                      joining them: four controls plus a name does not fit a
                      phone row, and reordering is what you do while waiting,
                      not while picking. Remove (×) stays either way. */}
                  {(isMyTurn || offlineMode) ? (
                    <button
                      onClick={() => handlePick(q.player_id)}
                      disabled={makePick.isPending || makeOfflinePick.isPending}
                      className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-accent text-white hover:bg-accent-hover active:scale-95 transition disabled:opacity-50"
                    >
                      Draft
                    </button>
                  ) : (
                    <>
                      <button onClick={() => moveQueue(q.player_id, 'up')} disabled={i === 0} className="text-text-muted hover:text-text-primary w-9 h-9 flex items-center justify-center rounded-lg active:bg-bg-secondary disabled:opacity-30" title="Move up">▲</button>
                      <button onClick={() => moveQueue(q.player_id, 'down')} disabled={i === visibleQueue.length - 1} className="text-text-muted hover:text-text-primary w-9 h-9 flex items-center justify-center rounded-lg active:bg-bg-secondary disabled:opacity-30" title="Move down">▼</button>
                    </>
                  )}
                  <button onClick={() => toggleQueue(q.player_id)} className="text-text-muted hover:text-incorrect w-9 h-9 flex items-center justify-center rounded-lg active:bg-bg-secondary text-lg" title="Remove">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        )
      })()}

      {activeTab === 'Log' && (
        <div className="rounded-xl border border-text-primary/20 overflow-hidden">
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-semibold text-text-primary">Draft Log</h3>
          </div>
          <DraftLogList completedPicks={completedPicks} numTeams={settings?.num_teams || 10} profileId={profile?.id} listRef={pickListRef} />
        </div>
      )}

      {/* Commissioner: autodraft prompt after an autopick */}
      {autoDraftPrompt && isCommissioner && (
        // Sized as a desktop corner toast, which doesn't survive a phone:
        // max-w-sm is nearly full width there, and bottom-4 put it on top of
        // the content — the Queue tab in particular — where it stayed
        // through scrolling until dismissed. Seen on a live draft
        // 2026-09-05. Now it clears the bottom nav and the home indicator on
        // small screens, and keeps the corner placement from md up.
        <div
          className="fixed bottom-24 left-4 right-4 md:bottom-4 md:left-auto md:right-4 z-50 rounded-xl border border-accent/40 bg-bg-card shadow-xl p-4 md:max-w-sm"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="text-sm text-text-primary mb-3">
            <span className="font-semibold">{autoDraftPrompt.displayName}</span> was auto-picked (pick #{autoDraftPrompt.pickNumber}). Are they autodrafting?
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try {
                  await setAutoDraftMut.mutateAsync({ leagueId: league.id, userId: autoDraftPrompt.userId, enabled: true })
                  toast(`${autoDraftPrompt.displayName} set to autodraft`, 'success')
                } catch (err) { toast(err.message || 'Failed', 'error') }
                setAutoDraftPrompt(null)
              }}
              disabled={setAutoDraftMut.isPending}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              Yes, autodraft
            </button>
            <button
              onClick={() => setAutoDraftPrompt(null)}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-bg-secondary text-text-secondary border border-text-primary/20 hover:bg-bg-card-hover transition-colors"
            >
              No
            </button>
          </div>
        </div>
      )}

    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Helpers

const STARTER_SLOT_LABELS = {
  qb: 'QB', rb: 'RB', wr: 'WR', te: 'TE', flex: 'FLEX', k: 'K', def: 'D/ST',
  superflex: 'SUPER FLEX',
  dl: 'DL', lb: 'LB', db: 'DB', s: 'S',
}
// Eligibility comes from lib/rosterSlots — the same map the server validates
// against. These were four separate local constants that had to be kept in
// sync by hand with the server's starterPlan.
const FLEX_ELIGIBLE = SLOT_ELIGIBILITY.flex
const SUPERFLEX_ELIGIBLE = SLOT_ELIGIBILITY.superflex
const IDP_ELIGIBLE = {
  dl: SLOT_ELIGIBILITY.dl,
  lb: SLOT_ELIGIBILITY.lb,
  db: SLOT_ELIGIBILITY.db,
  s: SLOT_ELIGIBILITY.s,
}

/**
 * Given the commissioner's roster_slots config and the user's drafted picks,
 * return an ordered list of starter slots with the player (or null) filling each,
 * plus the bench list. Greedy assignment: best player per primary slot first,
 * leftover RB/WR/TE flow into FLEX, leftovers go to bench.
 */
function buildSlotPlan(rosterSlots, myPicks) {
  const players = myPicks.map((p) => ({
    id: p.player_id,
    name: p.nfl_players?.full_name,
    position: p.nfl_players?.position,
    team: p.nfl_players?.team,
    headshot: p.nfl_players?.headshot_url,
  }))
  const remaining = [...players]
  function take(pos) {
    const idx = remaining.findIndex((p) => p.position === pos)
    if (idx >= 0) return remaining.splice(idx, 1)[0]
    return null
  }
  function takeAny(positions) {
    const idx = remaining.findIndex((p) => positions.includes(p.position))
    if (idx >= 0) return remaining.splice(idx, 1)[0]
    return null
  }

  const slots = [] // { slot, label, player }
  // IDP slots were missing entirely, so in an IDP league every defender fell
  // through to the bench and the roster total undercounted by the number of
  // IDP slots — Test 54 showed "My Roster (17/16)" against a real total of
  // 20. buildSlotMeta in FantasyMatchup already had these; this copy never
  // got them. Five places in this client build a slot list and they have
  // drifted; that drift is the actual bug and is worth consolidating.
  const order = ['qb', 'rb', 'wr', 'te', 'flex', 'superflex', 'k', 'def', 'dl', 'lb', 'db', 's']
  for (const slotKey of order) {
    const count = rosterSlots[slotKey] || 0
    for (let i = 0; i < count; i++) {
      let player = null
      if (slotKey === 'flex') player = takeAny(FLEX_ELIGIBLE)
      else if (slotKey === 'superflex') player = takeAny(SUPERFLEX_ELIGIBLE)
      // IDP positions need family matching — a 'dl' slot has to accept
      // DE/DT/NT, not just a literal 'DL'. take(slotKey.toUpperCase())
      // would only ever match the exact string.
      else if (IDP_ELIGIBLE[slotKey]) player = takeAny(IDP_ELIGIBLE[slotKey])
      else player = take(slotKey.toUpperCase())
      slots.push({ slot: slotKey, label: STARTER_SLOT_LABELS[slotKey] || slotKey.toUpperCase(), player })
    }
  }

  const benchCount = rosterSlots.bench || 0
  // Bench accepts ALL non-starter players, even beyond benchCount, so users
  // can punt K/DEF and the extras spill over into bench. Configured benchCount
  // is just the minimum slots to display — actual bench can grow.
  const bench = remaining
  const startersFilled = slots.filter((s) => s.player).length
  const totalStarters = slots.length
  const totalRoster = totalStarters + benchCount
  const filledTotal = startersFilled + bench.length
  return { slots, bench, overflow: [], filled: filledTotal, totalStarters, totalRoster, benchCount }
}

function RosterNeedsView({ slotPlan }) {
  // Compute "need" summary by counting empty starter slots per label
  const needCounts = {}
  for (const s of slotPlan.slots) {
    if (!s.player) needCounts[s.label] = (needCounts[s.label] || 0) + 1
  }
  const needList = Object.entries(needCounts)
  return (
    <div className="space-y-4">
      {needList.length > 0 && (
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-3 text-center">
          <span className="text-xs text-yellow-400 font-semibold uppercase tracking-wider">Still need: </span>
          <span className="text-xs text-text-primary">
            {needList.map(([label, n], i) => (
              <span key={label}>{i > 0 ? ', ' : ''}{n} {label}</span>
            ))}
          </span>
        </div>
      )}
      <div className="rounded-xl border border-text-primary/20 overflow-hidden">
        <div className="p-3 border-b border-border">
          <h3 className="text-sm font-semibold text-text-primary">Starting Lineup</h3>
        </div>
        <div className="divide-y divide-border">
          {slotPlan.slots.map((s, i) => (
            <SlotRow key={i} label={s.label} player={s.player} />
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-text-primary/20 overflow-hidden">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">Bench</h3>
          <span className="text-[10px] text-text-muted">{slotPlan.bench.length} / {slotPlan.benchCount}</span>
        </div>
        <div className="divide-y divide-border">
          {(() => {
            const rows = []
            slotPlan.bench.forEach((p) => rows.push(<SlotRow key={`b-${p.id}`} label="BN" player={p} />))
            const emptyToShow = Math.max(0, slotPlan.benchCount - slotPlan.bench.length)
            for (let i = 0; i < emptyToShow; i++) {
              rows.push(<SlotRow key={`e-${i}`} label="BN" player={null} />)
            }
            return rows
          })()}
        </div>
      </div>
    </div>
  )
}

function SlotRow({ label, player }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted w-12 shrink-0">{label}</span>
      {player ? (
        <>
          {player.headshot && (
            <img src={player.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-bg-card shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-text-primary truncate">{player.name}</div>
            <div className="text-[10px] text-text-muted">{player.position} · {player.team || 'FA'}</div>
          </div>
        </>
      ) : (
        <span className="text-xs text-text-muted italic">empty</span>
      )}
    </div>
  )
}

function DraftLogList({ completedPicks, numTeams, profileId, listRef }) {
  return (
    <div ref={listRef} className="max-h-[60vh] md:max-h-96 overflow-y-auto">
      {completedPicks.map((pick) => (
        <div
          key={pick.id}
          className={`flex items-center gap-3 px-3 py-2 border-b border-border last:border-0 ${
            pick.user_id === profileId ? 'bg-accent/5' : ''
          }`}
        >
          <span className="text-xs text-text-muted w-8 shrink-0">
            {pick.round}.{((pick.pick_number - 1) % numTeams) + 1}
          </span>
          {pick.nfl_players?.headshot_url && (
            <img
              src={pick.nfl_players.headshot_url}
              alt=""
              className="w-8 h-8 rounded-full object-cover bg-bg-secondary shrink-0"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary truncate">
              {pick.nfl_players?.full_name}
            </div>
            <div className="text-xs text-text-muted">
              {pick.nfl_players?.position} · {pick.nfl_players?.team} — {pick.users?.display_name || pick.users?.username}
              {pick.is_auto_pick && <span className="text-yellow-400 ml-1">(auto)</span>}
            </div>
          </div>
        </div>
      ))}
      {completedPicks.length === 0 && (
        <div className="text-center text-sm text-text-muted py-8">Waiting for first pick...</div>
      )}
    </div>
  )
}

function CompletedDraftReview({ league, picks, settings, profileId }) {
  const [openTeam, setOpenTeam] = useState(profileId)
  const numTeams = settings?.num_teams || 10
  const scoringFormat = settings?.scoring_format === 'half_ppr' ? 'Half-PPR' : settings?.scoring_format === 'standard' ? 'Standard' : 'PPR'

  // Group picks by user_id
  const teamMap = new Map()
  for (const pick of picks) {
    if (!teamMap.has(pick.user_id)) {
      teamMap.set(pick.user_id, {
        userId: pick.user_id,
        user: pick.users,
        picks: [],
      })
    }
    teamMap.get(pick.user_id).picks.push(pick)
  }
  // Order teams by draft order
  const draftOrder = settings?.draft_order || []
  const teams = draftOrder
    .map((uid, i) => {
      const t = teamMap.get(uid)
      return t ? { ...t, slot: i } : null
    })
    .filter(Boolean)

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-text-primary/20 bg-bg-primary/15 backdrop-blur-sm p-4 text-center">
        <div className="text-xs uppercase text-text-muted tracking-wider mb-1">Draft Complete</div>
        <div className="font-display text-lg text-text-primary">
          {numTeams}-team {scoringFormat}
        </div>
      </div>

      <div className="space-y-2">
        {teams.map((team) => {
          const isUser = team.userId === profileId
          const isOpen = openTeam === team.userId
          return (
            <div
              key={team.userId}
              className={`rounded-xl border ${isUser ? 'border-accent/40' : 'border-text-primary/20'} bg-bg-primary overflow-hidden`}
            >
              <button
                onClick={() => setOpenTeam(isOpen ? null : team.userId)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-bg-secondary"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-text-muted">#{team.slot + 1}</span>
                  <span className={`text-sm font-semibold ${isUser ? 'text-accent' : 'text-text-primary'}`}>
                    {isUser ? 'Your Team' : (team.user?.display_name || team.user?.username || 'Unknown')}
                  </span>
                </div>
                <span className="text-text-muted">{isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div className="border-t border-text-primary/10 divide-y divide-text-primary/10">
                  {team.picks.sort((a, b) => a.pick_number - b.pick_number).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 px-4 py-2">
                      <span className="text-[10px] text-text-muted w-10 shrink-0">{p.round}.{((p.pick_number - 1) % numTeams) + 1}</span>
                      {p.nfl_players?.headshot_url && (
                        <img src={p.nfl_players.headshot_url} alt="" className="w-7 h-7 rounded-full object-cover bg-bg-secondary shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{p.nfl_players?.full_name}</div>
                        <div className="text-[10px] text-text-muted">{p.nfl_players?.position} · {p.nfl_players?.team}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Full board for reference */}
      <div className="rounded-xl border border-text-primary/20 bg-bg-primary p-2 overflow-hidden">
        <DraftBoard picks={picks} settings={settings} profileId={profileId} />
      </div>
    </div>
  )
}

// Pre-draft player browser + queue manager. Self-contained: drives its own
// search/filter state and uses the same hooks as the in-progress UI. Tapping
// a player opens DraftPlayerPreview with a Queue button (Draft is gated to
// in-progress, so it's hidden here). Anything queued here flows through the
// same autodraft fallback once the draft is live.
function PreDraftBrowser({ leagueId }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [posFilter, setPosFilter] = useState('All')
  const [detailPlayerId, setDetailPlayerId] = useState(null)

  const { data: settings } = useFantasySettings(leagueId)
  const { data: availablePlayers } = useAvailablePlayers(
    leagueId,
    // Same one-character guard as the live draft room above.
    searchQuery.trim().length >= 2 ? searchQuery : undefined,
    posFilter !== 'All' ? posFilter : undefined,
  )
  const { data: queue } = useDraftQueue(leagueId)
  const setQueue = useSetDraftQueue()
  const queuedIds = useMemo(() => new Set((queue || []).map((q) => q.player_id)), [queue])

  async function toggleQueue(playerId) {
    const current = (queue || []).map((q) => q.player_id)
    const next = current.includes(playerId)
      ? current.filter((id) => id !== playerId)
      : [...current, playerId]
    try {
      await setQueue.mutateAsync({ leagueId, playerIds: next })
    } catch (err) {
      toast(err.message || 'Failed to update queue', 'error')
    }
  }

  return (
    <div className="space-y-4">
      {/* Player preview — Queue only, Draft is hidden until draft starts */}
      {detailPlayerId && (
        <DraftPlayerPreview
          leagueId={leagueId}
          playerId={detailPlayerId}
          onClose={() => setDetailPlayerId(null)}
          onQueue={() => toggleQueue(detailPlayerId)}
          isQueued={queuedIds.has(detailPlayerId)}
        />
      )}

      {/* Your queue */}
      {(queue || []).length > 0 && (
        <div className="rounded-xl border border-text-primary/20 bg-bg-primary/60 backdrop-blur-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">Your Queue</div>
            <span className="text-[10px] text-text-muted italic">Auto-picks from here when your clock runs out</span>
          </div>
          <div className="space-y-1.5">
            {queue.map((q, i) => (
              <div key={q.player_id} className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-bg-card">
                <span className="text-xs text-text-muted w-5 text-center shrink-0">{i + 1}</span>
                {q.nfl_players?.headshot_url && (
                  <img src={q.nfl_players.headshot_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" onError={(e) => { e.target.style.display = 'none' }} />
                )}
                <button onClick={() => setDetailPlayerId(q.player_id)} className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-text-primary truncate">{q.nfl_players?.full_name}</div>
                  <div className="text-[10px] text-text-muted">{q.nfl_players?.position} · {q.nfl_players?.team || 'FA'}</div>
                </button>
                <button onClick={() => toggleQueue(q.player_id)} className="text-text-muted hover:text-incorrect transition-colors text-lg leading-none w-8 h-8 flex items-center justify-center" title="Remove from queue">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Browse */}
      <div className="rounded-xl border border-text-primary/20 bg-bg-primary/60 backdrop-blur-sm overflow-hidden">
        <div className="p-3 border-b border-text-primary/10">
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Browse Players</div>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search players..."
            className="w-full bg-bg-card border border-text-primary/15 rounded-lg px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-hide">
            {buildDraftPositionFilters(settings?.roster_slots).map((pos) => (
              <button
                key={pos}
                onClick={() => setPosFilter(pos)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  posFilter === pos ? 'bg-accent text-white' : 'bg-bg-card text-text-secondary hover:bg-bg-secondary'
                }`}
              >{pos}</button>
            ))}
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {/* No cap. This was slice(0, 60), which meant the ONE screen whose
              purpose is building a queue before the draft showed the top 60
              players and nothing else — you couldn't queue a sleeper, a
              handcuff, or anyone past the fourth round. The live draft room
              renders the full list already, so this was also the more
              restrictive of the two despite being the one you use to
              prepare. The container scrolls (max-h-[55vh] overflow-y-auto)
              and the list is a few hundred rows, same as the live room. */}
          {(availablePlayers || []).map((player) => {
            const isQueued = queuedIds.has(player.id)
            return (
              // Row is a div, not a button: the star has to be its own
              // button and nesting buttons is invalid. Matches the live
              // draft room, which already had a row star — this list, the
              // one you actually build a queue in before the draft, made
              // you open each player's detail card just to queue them.
              <div
                key={player.id}
                className="w-full flex items-center gap-3 px-3 py-2.5 border-b border-text-primary/10 last:border-0 hover:bg-accent/10 transition-colors"
              >
                <span className="w-7 text-center text-xs font-bold text-text-muted shrink-0">
                  {player.adp_rank || '—'}
                </span>
                <button
                  onClick={() => toggleQueue(player.id)}
                  className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-lg active:bg-bg-secondary transition-colors ${isQueued ? 'text-yellow-400' : 'text-text-muted hover:text-yellow-400'}`}
                  title={isQueued ? 'Remove from queue' : 'Add to queue'}
                  aria-label={isQueued ? `Remove ${player.full_name} from queue` : `Add ${player.full_name} to queue`}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={isQueued ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
                <button
                  onClick={() => setDetailPlayerId(player.id)}
                  className="flex-1 flex items-center gap-3 text-left min-w-0"
                >
                {player.headshot_url && (
                  <img src={player.headshot_url} alt="" loading="lazy" className="w-9 h-9 rounded-full object-cover bg-bg-secondary shrink-0" onError={(e) => { e.target.style.visibility = 'hidden' }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary truncate">{player.full_name}</div>
                  <div className="text-[10px] text-text-muted">
                    <span className={`px-1 py-0.5 rounded text-[10px] font-bold mr-1 ${posColor(player.position) || 'text-text-muted'}`}>
                      {player.position}
                    </span>
                    {player.team || 'FA'}{player.bye_week ? ` · Bye ${player.bye_week}` : ''}
                  </div>
                </div>
                </button>
                {isQueued && (
                  <span className="text-[10px] font-bold text-correct shrink-0">QUEUED</span>
                )}
              </div>
            )
          })}
          {!(availablePlayers || []).length && (
            <div className="text-center text-sm text-text-muted py-8">No players match.</div>
          )}
        </div>
      </div>
    </div>
  )
}

const POS_COLORS = {
  QB: 'bg-red-500/20 border-red-500/40 text-red-300',
  RB: 'bg-green-500/20 border-green-500/40 text-green-300',
  WR: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-300',
  TE: 'bg-blue-500/20 border-blue-500/40 text-blue-300',
  K: 'bg-gray-500/20 border-gray-500/40 text-gray-300',
  DEF: 'bg-purple-500/20 border-purple-500/40 text-purple-300',
  // IDP had no colors at all, so DL/LB/DB rendered as plain grey text in
  // every IDP league.
  DL: 'bg-orange-500/20 border-orange-500/40 text-orange-300',
  LB: 'bg-teal-500/20 border-teal-500/40 text-teal-300',
  DB: 'bg-pink-500/20 border-pink-500/40 text-pink-300',
  S: 'bg-pink-500/20 border-pink-500/40 text-pink-300',
}

// Two-way players carry a slashed position like "WR/DB", which is not a key
// in POS_COLORS — so the badge lost its colour entirely once overrides
// started reaching these views. Colour comes from the FIRST part (the
// primary fantasy position); the label still shows the full string, so
// Travis Hunter reads "WR/DB" in receiver colours.
function posColor(pos) {
  const first = String(pos || '').split('/')[0].trim()
  return POS_COLORS[first] || ''
}

function DraftBoard({ picks, settings, profileId, presentUserIds }) {
  const numTeams = settings?.num_teams || 10
  const draftOrder = settings?.draft_order || []
  const totalRounds = Math.ceil(picks.length / numTeams)

  // Build grid: rows = rounds, columns = draft order position
  const grid = []
  for (let r = 0; r < totalRounds; r++) {
    const row = []
    const isReverse = r % 2 === 1 // snake
    for (let c = 0; c < numTeams; c++) {
      const colIdx = isReverse ? numTeams - 1 - c : c
      const pickNum = r * numTeams + colIdx + 1
      const pick = picks.find((p) => p.pick_number === pickNum)
      row.push(pick || null)
    }
    grid.push(row)
  }

  // Map user IDs to display names
  const userNames = {}
  for (const pick of picks) {
    if (pick.users) userNames[pick.user_id] = pick.users.display_name || pick.users.username
  }

  // Mobile: scroll both ways, larger cells. Desktop: fit container.
  return (
    <div className="overflow-auto max-h-[70vh] md:max-h-none md:overflow-visible">
      <table className="text-xs border-collapse md:w-full md:table-fixed">
        <thead>
          <tr>
            <th className="px-1 py-2 text-text-muted font-semibold text-center w-10 border border-border bg-bg-secondary sticky left-0 z-10">Rd</th>
            {draftOrder.map((userId, i) => (
              <th key={userId} className={`px-2 py-2 font-semibold text-center border border-border min-w-[120px] md:min-w-0 ${userId === profileId ? 'text-accent' : 'text-text-secondary'}`}>
                <div className="text-text-muted text-[10px]">{i + 1}</div>
                <div className="truncate">
                  <span className={presentUserIds?.has(userId) ? 'inline-block border border-correct/60 rounded px-1.5 py-0.5' : ''}>
                    {userNames[userId] || 'Team'}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, roundIdx) => (
            <tr key={roundIdx}>
              <td className="px-1 py-1 text-center text-text-muted font-semibold border border-border bg-bg-secondary sticky left-0 z-10">
                <div className="flex items-center gap-0.5 justify-center">
                  {roundIdx + 1}
                  <span className="text-[8px]">{roundIdx % 2 === 0 ? '→' : '←'}</span>
                </div>
              </td>
              {row.map((pick, colIdx) => {
                const pos = pick?.nfl_players?.position
                const colorClass = pos ? posColor(pos) : ''
                return (
                  <td key={colIdx} className={`px-2 py-2 border border-border min-w-[120px] md:min-w-0 ${pick ? colorClass : ''}`}>
                    {pick?.nfl_players ? (
                      <div className="min-w-0">
                        <div className="font-semibold truncate text-[11px] md:text-xs">{pick.nfl_players.full_name}</div>
                        <div className="text-[10px] opacity-70 truncate">{pos} {pick.nfl_players.team}</div>
                      </div>
                    ) : (
                      <div className="text-text-muted text-center">—</div>
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Pre-draft board preview (T-60min countdown) ──────────────────────

function DraftBoardPreview({ settings, picks, draftDate, profileId, league, isCommissioner, onStartDraft, startDraftPending, presentUserIds }) {
  const draftOrder = settings?.draft_order || []
  const rosterSlots = settings?.roster_slots || { qb: 1, rb: 2, wr: 2, te: 1, flex: 1, k: 1, def: 1, bench: 6 }
  // Exclude IR — you don't draft into an injured-reserve slot, you move
  // someone there later. Summing every slot drew one phantom round that no
  // pick could ever fill: The Friends League showed 17 rows against 96 real
  // picks (6 teams x 16 rounds). Matches generateDraftOrder on the server
  // and the live DraftBoard, both of which already skip 'ir'.
  const totalSlots = Object.entries(rosterSlots)
    .reduce((sum, [k, v]) => sum + (k === 'ir' ? 0 : (v || 0)), 0)

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  const remaining = Math.max(0, draftDate.getTime() - now)
  const mins = Math.floor(remaining / 60000)
  const secs = Math.floor((remaining % 60000) / 1000)

  const userNames = {}
  for (const pick of picks) {
    if (pick.users) userNames[pick.user_id] = pick.users.display_name || pick.users.username
  }
  const userSlot = draftOrder.indexOf(profileId)

  return (
    <div className="space-y-4">
      <div className="text-center py-6">
        <div className="text-[10px] uppercase tracking-widest text-text-muted mb-2">Draft starts in</div>
        <div className="font-display text-6xl md:text-7xl text-accent tabular-nums">
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </div>
        {settings?.draft_location && (
          <div className="mt-3 text-sm text-text-secondary">
            <span className="text-text-muted">Location:</span> <span className="font-semibold text-text-primary">{settings.draft_location}</span>
          </div>
        )}
        {userSlot >= 0 && (
          <div className="mt-2 text-sm text-text-secondary">
            You're picking at <span className="font-bold text-accent">#{userSlot + 1}</span>
          </div>
        )}
        {isCommissioner && remaining <= 0 && (
          <button
            onClick={onStartDraft}
            disabled={startDraftPending}
            className="mt-4 px-6 py-2.5 rounded-xl text-sm font-semibold bg-correct text-white hover:bg-correct/80 transition-colors disabled:opacity-50"
          >
            {startDraftPending ? 'Starting...' : 'Start Draft Now'}
          </button>
        )}
      </div>

      <div className="rounded-xl border border-text-primary/20 p-2 overflow-auto max-h-[70vh] md:max-h-none md:overflow-visible">
        <table className="text-xs border-collapse md:w-full md:table-fixed">
          <thead>
            <tr>
              <th className="px-1 py-2 text-text-muted font-semibold text-center w-10 border border-border bg-bg-secondary">Rd</th>
              {draftOrder.map((userId, i) => (
                <th key={userId} className={`px-2 py-2 font-semibold text-center border border-border min-w-[120px] md:min-w-0 ${userId === profileId ? 'text-accent' : 'text-text-secondary'}`}>
                  <div className="text-text-muted text-[10px]">{i + 1}</div>
                  <div className="truncate">
                    <span className={presentUserIds?.has(userId) ? 'inline-block border border-correct/60 rounded px-1.5 py-0.5' : ''}>
                      {userNames[userId] || 'Team'}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: totalSlots }, (_, r) => (
              <tr key={r}>
                <td className="px-1 py-3 text-center text-text-muted font-semibold border border-border bg-bg-secondary">
                  <div className="flex items-center gap-0.5 justify-center">
                    {r + 1}
                    <span className="text-[8px]">{r % 2 === 0 ? '\u2192' : '\u2190'}</span>
                  </div>
                </td>
                {draftOrder.map((userId) => (
                  <td key={userId} className="px-2 py-3 border border-border text-center min-w-[120px] md:min-w-0">
                    <div className="text-text-muted/30">{'\u2014'}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pre-draft browser \u2014 search, queue, tap-to-inspect */}
      <PreDraftBrowser leagueId={league.id} />
    </div>
  )
}
